import {useRef, type ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {AnankeTempPageTitle} from "~/components/anankeTemp/anankeTempPageTitle";
import {Breadcrumbs} from "~/components/breadcrumbs";
import {
    AsidePanelFullscreenColumn,
    ResizableAsidePanelColumn,
} from "~/components/resizableAsidePanelColumn";
import {AsidePanelProvider, useAsidePanelContext} from "~/contexts/asidePanelContext";

export function TableOnlyPage({
    breadcrumbItems,
    breadcrumbSection,
    title,
    subtitle,
    titleRightSection,
    bottomSection,
    hideTitleSection = false,
    stickyTitleSection = false,
}: {
    /** Ottopilot-style crumbs rendered above the page title. */
    breadcrumbItems?: Array<{
        displayName: string;
        to: string | null;
    }>;
    /** Custom breadcrumbs above the page title (e.g. button-based folder nav). */
    breadcrumbSection?: ReactNode;
    title: string;
    subtitle?: string;
    titleRightSection?: ReactNode;
    bottomSection: ReactNode;
    /** When true, the page title row and spacer are hidden (e.g. when a modal with its own header is shown). */
    hideTitleSection?: boolean;
    /** When true, page title stays visible while the main content scrolls. */
    stickyTitleSection?: boolean;
}) {
    return (
        <AsidePanelProvider>
            <TableOnlyPageInner
                breadcrumbItems={breadcrumbItems}
                breadcrumbSection={breadcrumbSection}
                title={title}
                subtitle={subtitle}
                titleRightSection={titleRightSection}
                bottomSection={bottomSection}
                hideTitleSection={hideTitleSection}
                stickyTitleSection={stickyTitleSection}
            />
        </AsidePanelProvider>
    );
}

function TableOnlyPageInner({
    breadcrumbItems,
    breadcrumbSection,
    title,
    subtitle,
    titleRightSection,
    bottomSection,
    hideTitleSection = false,
    stickyTitleSection = false,
}: {
    breadcrumbItems?: Array<{
        displayName: string;
        to: string | null;
    }>;
    breadcrumbSection?: ReactNode;
    title: string;
    subtitle?: string;
    titleRightSection?: ReactNode;
    bottomSection: ReactNode;
    hideTitleSection?: boolean;
    stickyTitleSection?: boolean;
}) {
    const layoutRef = useRef<HTMLDivElement>(null);
    const {state} = useAsidePanelContext();
    const isOpen = state.isOpen;
    const isFullscreen = state.isFullscreen;

    const shouldShowAsideColumn = isOpen === true;
    const shouldHideMainSection = isOpen === true && isFullscreen === true;
    const showResizableAside = shouldHideMainSection !== true;

    const resolvedBreadcrumbSection =
        breadcrumbSection !== undefined && breadcrumbSection !== null
            ? breadcrumbSection
            : breadcrumbItems !== undefined && breadcrumbItems.length > 0
              ? <Breadcrumbs items={breadcrumbItems} />
              : null;

    return (
        <div ref={layoutRef} className="flex min-h-0 h-full w-full overflow-hidden">
            {/* Keep the main section mounted (hidden when fullscreen) so the aside panel portal
                that lives inside it does not unmount and flip `isFullscreen`, which would loop. */}
            <div className={twJoin("min-h-0 h-full min-w-0 flex-1", shouldHideMainSection === true && "hidden")}>
                <div
                    className={twJoin(
                        "table-only-page-footer-border-target",
                        hideTitleSection === true
                            ? "grid min-h-0 h-full w-full grid-cols-1 grid-rows-[auto_minmax(0,1fr)] overflow-y-hidden pt-ds-space-200 sm:pt-ds-space-300"
                            : "grid min-h-0 h-full w-full grid-cols-1 grid-rows-[auto_minmax(0,1fr)] overflow-y-hidden pt-ds-space-200 sm:pt-ds-space-300",
                    )}
                >
                    {hideTitleSection === false ? (
                        <div
                            className={twJoin(
                                "grid gap-y-0 px-ds-space-200 sm:px-ds-space-300",
                                stickyTitleSection === true &&
                                    "sticky top-0 z-30 bg-ds-elevation-surface-current pb-ds-space-100",
                            )}
                        >
                            {resolvedBreadcrumbSection}
                            <div
                                className={twJoin(
                                    "grid min-h-10 grid-cols-[minmax(0,1fr)_auto] gap-x-ds-space-300",
                                    subtitle !== undefined && subtitle !== "" ? "items-start" : "items-center",
                                )}
                            >
                                <div>
                                    <AnankeTempPageTitle title={title} />
                                    {subtitle !== undefined && subtitle !== "" ? (
                                        <p className="mt-ds-space-050 text-sm text-ds-text-subtle">{subtitle}</p>
                                    ) : null}
                                </div>
                                {titleRightSection !== undefined ? <div>{titleRightSection}</div> : null}
                            </div>
                        </div>
                    ) : (
                        <div />
                    )}

                    <div className="flex min-h-0 h-full min-w-0 w-full flex-col">{bottomSection}</div>
                </div>
            </div>

            {shouldHideMainSection === true && shouldShowAsideColumn === true ? (
                <AsidePanelFullscreenColumn />
            ) : (
                <ResizableAsidePanelColumn
                    layoutRef={layoutRef}
                    isVisible={shouldShowAsideColumn && showResizableAside}
                />
            )}
        </div>
    );
}
